import React, { useState } from 'react';
import { ArrowRight, Check, Clock, Minus, Users, LayoutGrid } from 'lucide-react';
import { RosterModal } from './RosterModal';
import { ActionPanel, ActionFormData } from './ActionPanel';
import { useToast } from './Toast';
interface DashboardViewProps {
  onNavigate: (page: string) => void;
}
interface OffenderSite {
  id: string;
  rank: number;
  name: string;
  code: string;
  manager: string;
  plannedHours: number;
  exceptionHours: number;
  totalHours: number;
  overCount: number;
  emptyCount: number;
  status: 'pending' | 'done' | 'none';
}
const offenders: OffenderSite[] = [{
  id: 'woolworths-hq',
  rank: 1,
  name: 'Woolworths HQ',
  code: 'WC 01',
  manager: 'Sarah van Wyk',
  plannedHours: 400,
  exceptionHours: 104,
  totalHours: 504,
  overCount: 8,
  emptyCount: 3,
  status: 'pending'
}, {
  id: 'std-bank',
  rank: 2,
  name: 'Standard Bank Tower',
  code: 'GP 02',
  manager: 'James Nkosi',
  plannedHours: 320,
  exceptionHours: 76,
  totalHours: 396,
  overCount: 6,
  emptyCount: 1,
  status: 'done'
}, {
  id: 'pick-n-pay',
  rank: 3,
  name: 'Pick n Pay Constantia',
  code: 'WC 03',
  manager: 'Thabo Molefe',
  plannedHours: 250,
  exceptionHours: 62,
  totalHours: 312,
  overCount: 5,
  emptyCount: 2,
  status: 'none'
}, {
  id: 'nedbank-plaza',
  rank: 4,
  name: 'Nedbank Plaza',
  code: 'GP 04',
  manager: 'Linda Khumalo',
  plannedHours: 230,
  exceptionHours: 58,
  totalHours: 288,
  overCount: 4,
  emptyCount: 0,
  status: 'done'
}, {
  id: 'checkers-claremont',
  rank: 5,
  name: 'Checkers Claremont',
  code: 'WC 05',
  manager: 'Sipho Dlamini',
  plannedHours: 210,
  exceptionHours: 54,
  totalHours: 264,
  overCount: 3,
  emptyCount: 1,
  status: 'pending'
}, {
  id: 'fnb-building',
  rank: 6,
  name: 'FNB Building',
  code: 'GP 06',
  manager: 'Nomsa Khumalo',
  plannedHours: 190,
  exceptionHours: 50,
  totalHours: 240,
  overCount: 3,
  emptyCount: 2,
  status: 'none'
}, {
  id: 'spar-distribution',
  rank: 7,
  name: 'Spar Distribution Centre',
  code: 'KZN 07',
  manager: 'Thabo Molefe',
  plannedHours: 180,
  exceptionHours: 36,
  totalHours: 216,
  overCount: 2,
  emptyCount: 1,
  status: 'done'
}, {
  id: 'dischem-warehouse',
  rank: 8,
  name: 'Dischem Warehouse',
  code: 'GP 08',
  manager: 'Bongani Zulu',
  plannedHours: 160,
  exceptionHours: 32,
  totalHours: 192,
  overCount: 2,
  emptyCount: 0,
  status: 'pending'
}];
export function DashboardView({
  onNavigate
}: DashboardViewProps) {
  const maxTotalHours = Math.max(...offenders.map(o => o.totalHours));
  const [selectedSite, setSelectedSite] = useState<OffenderSite | null>(null);
  const [actionPanelSite, setActionPanelSite] = useState<string | null>(null);
  const {
    showToast
  } = useToast();
  const handleTakeAction = (siteName: string) => {
    setSelectedSite(null);
    // Open the action panel on the next tick so the modal close animation reads cleanly
    setActionPanelSite(siteName);
  };
  const handleActionSubmit = (_data: ActionFormData) => {
    setActionPanelSite(null);
    showToast('Action created successfully', 'success');
  };
  return <div className="flex flex-col gap-6 animate-in fade-in duration-300" data-id="element-466">
      {/* Top Widgets Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch" data-id="element-467">
        {/* Widget 1: Overtime Health */}
        <div onClick={() => onNavigate('overtime-cost')} className="bg-white rounded-2xl p-6 border border-[#e5e5e5] shadow-sm hover:shadow-md transition-all cursor-pointer flex flex-col justify-between h-full" data-id="element-468">
          <div data-id="element-469">
            <h3 className="text-xs font-semibold text-[#5a7280] uppercase tracking-wider mb-4" data-id="element-470">
              Overtime Health
            </h3>
            <span className="text-4xl font-bold text-[#062133] block mb-2" data-id="element-471">
              20,195 hrs
            </span>
            <p className="text-lg font-medium text-[#ff697f] mb-6" data-id="element-472">
              R 335,237 total cost
            </p>
          </div>

          <div className="pt-4 border-t border-[#e5e5e5]" data-id="element-473">
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700" data-id="element-474">
              ↘ 30% vs prev cycle
            </span>
          </div>
        </div>

        {/* Widget 2: Guard Balance */}
        <div onClick={() => onNavigate('guards-threshold')} className="bg-white rounded-2xl p-6 border border-[#e5e5e5] shadow-sm hover:shadow-md transition-all cursor-pointer flex flex-col justify-between h-full" data-id="element-475">
          <div data-id="element-476">
            <h3 className="text-xs font-semibold text-[#5a7280] uppercase tracking-wider mb-4" data-id="element-477">
              Guard Balance
            </h3>
            <div className="grid grid-cols-2 gap-4 mb-6" data-id="element-478">
              <div data-id="element-479">
                <span className="text-4xl font-bold text-[#062133] block mb-1" data-id="element-480">
                  1,054
                </span>
                <div className="flex items-center gap-1.5" data-id="element-481">
                  <div className="w-2 h-2 rounded-full bg-rose-500" data-id="element-482" />
                  <span className="text-sm text-[#5a7280]" data-id="element-483">Over 208h</span>
                </div>
              </div>
              <div data-id="element-484">
                <span className="text-4xl font-bold text-[#062133] block mb-1" data-id="element-485">
                  3,642
                </span>
                <div className="flex items-center gap-1.5" data-id="element-486">
                  <div className="w-2 h-2 rounded-full bg-sky-500" data-id="element-487" />
                  <span className="text-sm text-[#5a7280]" data-id="element-488">Under 208h</span>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 pt-4 border-t border-[#e5e5e5]" data-id="element-489">
            <div data-id="element-490">
              <p className="text-sm font-semibold text-[#062133]" data-id="element-491">20,195 hrs</p>
              <p className="text-xs text-[#5a7280]" data-id="element-492">Over threshold capacity</p>
            </div>
            <div data-id="element-493">
              <p className="text-sm font-semibold text-[#062133]" data-id="element-494">5,389 hrs</p>
              <p className="text-xs text-[#5a7280]" data-id="element-495">Spare capacity</p>
            </div>
          </div>
        </div>

        {/* Widget 3: Site Coverage */}
        <div onClick={() => onNavigate('sites-optimisation')} className="bg-white rounded-2xl p-6 border border-[#e5e5e5] shadow-sm hover:shadow-md transition-all cursor-pointer flex flex-col justify-between h-full" data-id="element-496">
          <div data-id="element-497">
            <h3 className="text-xs font-semibold text-[#5a7280] uppercase tracking-wider mb-4" data-id="element-498">
              Site Coverage
            </h3>
            <span className="text-4xl font-bold text-[#062133] block mb-1" data-id="element-499">
              356 sites flagged
            </span>
            <p className="text-sm text-[#5a7280] mb-6" data-id="element-500">
              83 empty rotation slots
            </p>
          </div>

          <div className="pt-4 border-t border-[#e5e5e5]" data-id="element-501">
            <div className="h-3 w-full flex rounded-full overflow-hidden mb-3 bg-[#fafaf9]" data-id="element-502">
              <div className="bg-emerald-400 h-full" style={{
              width: `${283 / 356 * 100}%`
            }} data-id="element-503" />
              <div className="bg-[#ff697f] h-full" style={{
              width: `${73 / 356 * 100}%`
            }} data-id="element-504" />
            </div>
            <div className="flex items-center justify-between" data-id="element-505">
              <span className="text-sm font-medium text-emerald-600" data-id="element-506">
                283 with actions
              </span>
              <span className="text-sm font-medium text-[#ff697f]" data-id="element-507">
                73 unactioned
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Worst Offenders Table */}
      <div className="bg-white rounded-2xl border border-[#e5e5e5] shadow-sm overflow-hidden" data-id="element-508">
        <div className="p-6 border-b border-[#e5e5e5]" data-id="element-509">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-510">
            <div data-id="element-511">
              <h2 className="text-xl font-bold text-[#062133]" data-id="element-512">
                Worst Offenders
              </h2>
              <p className="text-sm text-[#5a7280] mt-1" data-id="element-513">
                Top sites by overtime hours · 16 Jan – 15 Feb 2026
              </p>
            </div>
            <button onClick={() => onNavigate('sites-optimisation')} className="text-sm font-medium text-[#5a7280] hover:text-[#062133] flex items-center gap-1 transition-colors" data-id="element-514">
              View all sites <ArrowRight className="w-4 h-4" data-id="element-515" />
            </button>
          </div>
        </div>

        <div className="overflow-x-auto" data-id="element-516">
          <table className="w-full text-left border-collapse" data-id="element-517">
            <thead data-id="element-518">
              <tr className="bg-[#fafaf9] border-b border-[#e5e5e5]" data-id="element-519">
                <th className="py-4 px-6 text-xs font-semibold text-[#5a7280] uppercase w-12 text-center" data-id="element-520">
                  #
                </th>
                <th className="py-4 px-6 text-xs font-semibold text-[#5a7280] uppercase w-64" data-id="element-521">
                  Site
                </th>
                <th className="py-4 px-6 text-xs font-semibold text-[#5a7280] uppercase" data-id="element-522">
                  <div className="flex items-center gap-4" data-id="element-523">
                    <span data-id="element-524">Overtime Hours</span>
                    <div className="flex items-center gap-3 font-normal normal-case" data-id="element-525">
                      <div className="flex items-center gap-1.5" data-id="element-526">
                        <div className="w-2 h-2 bg-[#3a4a54]" data-id="element-527" />
                        <span className="text-[#5a7280]" data-id="element-528">Planned</span>
                      </div>
                      <div className="flex items-center gap-1.5" data-id="element-529">
                        <div className="w-2 h-2 bg-[#8a9aa4]" data-id="element-530" />
                        <span className="text-[#5a7280]" data-id="element-531">Exception</span>
                      </div>
                    </div>
                  </div>
                </th>
                <th className="py-4 px-6 text-xs font-semibold text-[#5a7280] uppercase w-24 text-center" data-id="element-532">
                  <div className="flex items-center justify-center gap-1.5" data-id="element-533">
                    <Users className="w-4 h-4" data-id="element-534" /> Over
                  </div>
                </th>
                <th className="py-4 px-6 text-xs font-semibold text-[#5a7280] uppercase w-24 text-center" data-id="element-535">
                  <div className="flex items-center justify-center gap-1.5" data-id="element-536">
                    <LayoutGrid className="w-4 h-4" data-id="element-537" /> Empty
                  </div>
                </th>
                <th className="py-4 px-6 text-xs font-semibold text-[#5a7280] uppercase w-24 text-center" data-id="element-538">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#e5e5e5]" data-id="element-539">
              {offenders.map(site => <tr key={site.id} className="hover:bg-[#fafaf9] transition-colors cursor-pointer" onClick={() => setSelectedSite(site)} data-id="element-540">
                  <td className="py-4 px-6 text-xl font-bold text-[#e5e5e5] text-center" data-id="element-541">
                    {site.rank}
                  </td>
                  <td className="py-4 px-6" data-id="element-542">
                    <p className="text-sm font-semibold text-[#062133]" data-id="element-543">
                      {site.name}
                    </p>
                    <p className="text-xs text-[#5a7280] mt-0.5" data-id="element-544">
                      {site.code} · {site.manager}
                    </p>
                  </td>
                  <td className="py-4 px-6" data-id="element-545">
                    <div className="flex items-center gap-4" data-id="element-546">
                      <div className="flex-1 h-6 flex rounded overflow-hidden bg-transparent" data-id="element-547">
                        <div className="bg-[#3a4a54] h-full" style={{
                      width: `${site.plannedHours / maxTotalHours * 100}%`
                    }} data-id="element-548" />
                        <div className="bg-[#8a9aa4] h-full" style={{
                      width: `${site.exceptionHours / maxTotalHours * 100}%`
                    }} data-id="element-549" />
                      </div>
                      <span className="text-sm font-bold text-[#062133] w-12 text-right" data-id="element-550">
                        {site.totalHours}h
                      </span>
                    </div>
                  </td>
                  <td className="py-4 px-6 text-center" data-id="element-551">
                    <div className="flex items-center justify-center gap-1.5 text-sm font-semibold text-rose-600" data-id="element-552">
                      <Users className="w-4 h-4" data-id="element-553" /> {site.overCount}
                    </div>
                  </td>
                  <td className="py-4 px-6 text-center" data-id="element-554">
                    <div className="flex items-center justify-center gap-1.5 text-sm font-semibold text-amber-600" data-id="element-555">
                      <LayoutGrid className="w-4 h-4" data-id="element-556" /> {site.emptyCount}
                    </div>
                  </td>
                  <td className="py-4 px-6 text-center" data-id="element-557">
                    <div className="flex justify-center" data-id="element-558">
                      {site.status === 'pending' && <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center text-amber-600" data-id="element-559">
                          <Clock className="w-4 h-4" data-id="element-560" />
                        </div>}
                      {site.status === 'done' && <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600" data-id="element-561">
                          <Check className="w-4 h-4" data-id="element-562" />
                        </div>}
                      {site.status === 'none' && <div className="w-8 h-8 rounded-full bg-rose-100 flex items-center justify-center text-rose-600" data-id="element-563">
                          <Minus className="w-4 h-4" data-id="element-564" />
                        </div>}
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>

      {/* Roster Modal */}
      <RosterModal isOpen={selectedSite !== null} siteName={selectedSite?.name || ''} siteId={selectedSite?.id || ''} onClose={() => setSelectedSite(null)} onTakeAction={() => {
      if (selectedSite) handleTakeAction(selectedSite.name);
    }} data-id="element-565" />

      {/* Action Panel (opened from RosterModal "Take Action") */}
      <ActionPanel isOpen={actionPanelSite !== null} siteName={actionPanelSite || ''} mode="site-prefilled" onClose={() => setActionPanelSite(null)} onSubmit={handleActionSubmit} data-id="element-566" />
    </div>;
}